<script lang="ts" setup>
import { ref, computed, watch } from "vue";
import _ from "lodash";

import { getDataAvailability } from "@/services/api/chart.api";
import useMessages from "@/composables/messages.js";
import PercentageChart from "@/components/app/charts/PercentageChart.vue";
import { useToast } from "vue-toastification";
import { severityCode } from "@/services/api/httpClient";

const { msgDataScore } = useMessages();

const props = defineProps({
  token: String,
  compact: {
    type: Boolean,
    default: false,
  },
});

const chart = ref({
  messages: [],
  percent: 0,
});
const isLoading = ref(true);
const hasError = ref(false);
const toaster = useToast();
const hasDataAvailabilityMessages = computed(() => {
  return _.isEmpty(chart.value.messages) === false;
});

watch(
  () => props.token,
  () => {
    fetchDataAvailabilityData();
  },
  { immediate: true }
);

async function fetchDataAvailabilityData() {
  isLoading.value = true;
  hasError.value = false;
  const response = await getDataAvailability(props.token);
  if (response && response.severity <= severityCode.warning) {
    chart.value = response.data;
    isLoading.value = false;
  } else {
    isLoading.value = false;
    hasError.value = true;
    toaster.error("Ophalen van de availability data is mislukt!");
  }
}
</script>
<template>
  <div class="widget-item" :class="{ 'widget-item--compact': props.compact }">
    <header>
      <h2>Volledigheid data</h2>
      <div class="widget-actions">
        <information-dropdown>
          <template v-slot:header>
            <h2>Goed op weg!</h2>
          </template>
          <p>
            Betrouwbare duurzaamheidsdata helpt om onderbouwde beslissingen te nemen in de verduurzamingsstrategie. INNAX begint daarom met het in kaart brengen van de beschikbare data en het bepalen
            van de kwaliteit hiervan. Belangrijke aspecten daarbij zijn:
          </p>
          <ul>
            <li>Volledigheid</li>
            <li>Juistheid</li>
            <li>Actualiteit</li>
          </ul>
          <br />
          <p>De Paris Proof Monitor toont hier een aantal verbeterslagen voor uw portefeuille waar deze nog geen volledige data dekking/coverage heeft.</p>
        </information-dropdown>
      </div>
    </header>
    <loader-spinner v-if="isLoading" :is-loading="isLoading"></loader-spinner>
    <section v-else-if="hasError" class="widget-wrapper" :min-height="200">
      <v-container class="pa-0">
        <p>Geen gegevens beschikbaar voor deze partij</p>
      </v-container>
    </section>
    <section v-else class="widget-wrapper" :min-height="200">
      <div v-show="hasDataAvailabilityMessages" class="widget-info-header">
        <h3>Dit missen we nog</h3>
        <span>Voor &eacute;&eacute;n of meerdere panden geldt</span>
      </div>
      <div class="compact-content">
        <div class="widget-chart data-availability">
          <percentage-chart key="donutChartOverviewOfBuildings" :percentage="chart.percent" :radius="props.compact ? 132 : 200"></percentage-chart>
        </div>
        <div v-show="hasDataAvailabilityMessages" class="widget-info-list">
          <ul>
            <li v-for="(msg, index) in chart.messages" :key="index">
              {{ msgDataScore(msg) }}
            </li>
          </ul>
        </div>
      </div>
    </section>
  </div>
</template>

<style lang="scss" scoped>
.widget-item--compact .widget-wrapper {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  margin-top: 4px;
}

.widget-item--compact .widget-info-header {
  flex: 0 0 auto;
  margin-bottom: 6px;
}

.widget-item--compact .widget-info-header h3 {
  margin-bottom: 0;
  min-height: auto;
  font-size: 1rem;
  line-height: 1;
}

.widget-item--compact .widget-info-header span {
  display: block;
  font-size: 0.86rem;
  line-height: 1;
  margin-bottom: 0;
}

.widget-item--compact .compact-content {
  flex: 1;
  min-height: 0;
  display: flex;
  gap: 10px;
}

.widget-item--compact .widget-chart.data-availability {
  flex: 0 0 142px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.widget-item--compact .widget-info-list {
  flex: 1;
  min-height: 0;
  overflow: auto;
  margin-top: 40px;
}

.widget-item--compact .widget-info-list ul {
  margin: 0;
  padding-left: 16px;
  list-style: disc;
}

.widget-item--compact .widget-info-list li {
  margin-bottom: 1px;
  font-size: 0.85rem;
  line-height: 1.2;
}
</style>
